import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PlaybackStatus, playbackRequestFromParams } from "../playback/providers";
import { catalog, ContentType } from "../api/tmdb";
import { resolvePlayback } from "../playback/registry";
import { parsePlayerEvent, isVidkingOrigin } from "../playback/playerEvents";
import { saveProgress } from "../storage/progress";
import TorrentVideo from "../components/TorrentVideo";
import YoutubePlayer from "../components/YoutubePlayer";

function providerFriendlyMessage(message) {
  if (/no sources/i.test(message)) return "No source is currently available for this title. This is a provider availability issue.";
  if (/server unavailable/i.test(message)) return "The current playback server is unavailable. Try again shortly.";
  if (/network/i.test(message)) return "The player could not connect. Check the network and try again.";
  return message || "This title cannot be played by the current provider right now.";
}

export default function PlayerScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const baseRequest = useMemo(() => playbackRequestFromParams(params), [params]);
  // `?lang=` carries the title's original language from every detail screen.
  // Continue Watching has none to pass (progress records hold no language) and
  // older links predate the param, so fetch it once here and merge it in — the
  // player needs it to pick the film's own audio out of a dub list.
  const [langFallback, setLangFallback] = useState(null);
  useEffect(() => {
    if (baseRequest.originalLanguage || !baseRequest.tmdbId) return undefined;
    let cancelled = false;
    const load =
      baseRequest.contentType === ContentType.EPISODE
        ? catalog.tvDetails(baseRequest.tmdbId)
        : catalog.movieDetails(baseRequest.tmdbId);
    load
      .then((details) => {
        if (!cancelled) setLangFallback(details?.originalLanguage || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [baseRequest.tmdbId, baseRequest.contentType, baseRequest.originalLanguage]);
  const request = useMemo(
    () =>
      baseRequest.originalLanguage || !langFallback
        ? baseRequest
        : { ...baseRequest, originalLanguage: langFallback },
    [baseRequest, langFallback],
  );
  const back = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  const [state, setState] = useState({
    resolving: true,
    sourceUrl: null,
    source: null,
    error: null,
  });
  const [reloadKey, setReloadKey] = useState(0);
  const [switching, setSwitching] = useState(false);
  const sourceRef = useRef(null);
  // Providers that failed at RUNTIME (hosted HLS CDN-blocked/dead, or the
  // Vidking embed's fatal "server unavailable"). Accumulated across the
  // session and passed as `exclude` so the chain walks past them — a hosted
  // stream that dies mid-page falls through to Vidking instead of a dead end.
  const excludedRef = useRef(new Set());
  // Has the hosted provider already spent its one fresh-token re-mint for this
  // title? Reset when the request changes so a new title starts clean.
  const remintTriedRef = useRef(false);
  useEffect(() => {
    remintTriedRef.current = false;
    excludedRef.current = new Set();
  }, [request]);
  useEffect(() => {
    sourceRef.current = state.source;
  }, [state.source]);

  useEffect(() => {
    let cancelled = false;
    setSwitching(false);
    setState({ resolving: true, sourceUrl: null, source: null, error: null });
    resolvePlayback(request, { exclude: [...excludedRef.current] })
      .then((source) => {
        if (cancelled) return;
        if (source.status === PlaybackStatus.RESOLVED && source.url) {
          setState({ resolving: false, sourceUrl: source.url, source, error: null });
        } else {
          setState({
            resolving: false,
            sourceUrl: null,
            error: source.errorMessage || "Playback unavailable",
          });
        }
      })
      .catch(() => {
        if (!cancelled) setState({ resolving: false, sourceUrl: null, error: "Playback unavailable" });
      });
    return () => {
      cancelled = true;
    };
  }, [request, reloadKey]);

  // Hosted-file streams that die AT RUNTIME — an expired token, a CDN 403, the
  // file pulled, a browser blocker — get ONE chance to recover on their own
  // source before we abandon the provider, because "hosted" is the admin's
  // chosen copy: their upload, their quality, their subtitles. Giving up on it
  // the instant a token expires silently swaps the viewer onto a third-party
  // embed, which is the worst possible outcome for a title we already own.
  //
  // So: first death -> re-resolve with `fresh: true`, which makes the server
  // mint a brand-new StreamTape token instead of serving the cached one. If
  // THAT fails too, the link is genuinely unusable and we fall through to the
  // next provider exactly as before.
  const onHostedStreamDead = useCallback(() => {
    const current = sourceRef.current;
    if (!current || current.provider !== "hosted") return;
    if (excludedRef.current.has("hosted")) return; // already walked past it
    setSwitching(true);
    const remint = !remintTriedRef.current;
    if (remint) remintTriedRef.current = true;
    resolvePlayback(request, {
      exclude: remint ? [] : ["hosted"],
      fresh: remint,
    })
      .then((source) => {
        if (source.status === PlaybackStatus.RESOLVED && source.url) {
          // Recovered on the admin's own copy. Stay eligible for one more
          // re-mint attempt if this one dies too — the token may simply have
          // been minted mid-rotation.
          if (remint) remintTriedRef.current = false;
          setState({ resolving: false, sourceUrl: source.url, source, error: null });
        } else {
          if (!remint) excludedRef.current.add("hosted");
          setState({
            resolving: false,
            sourceUrl: null,
            error: source.errorMessage || "Playback unavailable",
          });
        }
      })
      .catch(() => {
        if (!remint) excludedRef.current.add("hosted");
        setState({ resolving: false, sourceUrl: null, error: "Playback unavailable" });
      })
      .finally(() => setSwitching(false));
  }, [request]);

  // Runtime provider-failure fallback. The proxied Vidking player posts a
  // "poku:provider-dead" message (watcher injected by playback-proxy.js) when
  // its own UI reports a fatal source error — "server unavailable". Instead
  // of leaving the user on that error, re-resolve on the remaining providers
  // (order: TorBox -> torrent -> SuperEmbed), which in practice means
  // SuperEmbed takes over. If the fallback is also unavailable, the normal
  // "Playback unavailable / Try again" screen still shows — there is simply
  // nothing left to try.
  useEffect(() => {
    const onProviderDead = (event) => {
      const data = event.data;
      if (!data || data.type !== "poku:provider-dead") return;
      if (event.origin !== window.location.origin) return; // our proxied player only
      const current = sourceRef.current;
      if (!current || current.provider !== data.provider) return; // stale/other provider
      if (excludedRef.current.has(data.provider)) return; // already handled
      excludedRef.current.add(data.provider);
      setSwitching(true);
      resolvePlayback(request, { exclude: [...excludedRef.current] })
        .then((source) => {
          if (source.status === PlaybackStatus.RESOLVED && source.url) {
            setState({ resolving: false, sourceUrl: source.url, source, error: null });
          } else {
            setState({
              resolving: false,
              sourceUrl: null,
              error: source.errorMessage || "Playback unavailable",
            });
          }
        })
        .catch(() => {
          setState({ resolving: false, sourceUrl: null, error: "Playback unavailable" });
        })
        .finally(() => setSwitching(false));
    };
    window.addEventListener("message", onProviderDead);
    return () => window.removeEventListener("message", onProviderDead);
  }, [request]);

  // Stalled-embed fallback: a VidLink embed that loads but NEVER posts any
  // PLAYER_EVENT (their player spins forever on a title it has no sources
  // for) used to hang on "Preparing playback…" indefinitely. EmbedPlayer's
  // watchdog reports the stall; we walk the chain past that provider —
  // hosted/youtube/torbox/torrent already answered on the first resolve, so
  // this lands on SuperEmbed. If nothing is left, the normal error screen
  // shows — there is simply nothing left to try.
  const onEmbedStalled = useCallback(() => {
    const current = sourceRef.current;
    if (!current) return;
    if (excludedRef.current.has(current.provider)) return; // already handled
    excludedRef.current.add(current.provider);
    setSwitching(true);
    resolvePlayback(request, { exclude: [...excludedRef.current] })
      .then((source) => {
        if (source.status === PlaybackStatus.RESOLVED && source.url) {
          setState({ resolving: false, sourceUrl: source.url, source, error: null });
        } else {
          setState({
            resolving: false,
            sourceUrl: null,
            error: source.errorMessage || "Playback unavailable",
          });
        }
      })
      .catch(() => {
        setState({ resolving: false, sourceUrl: null, error: "Playback unavailable" });
      })
      .finally(() => setSwitching(false));
  }, [request]);

  // Pre-warm the hosted/hybrid path as soon as the player screen mounts: a
  // hybrid source mints a signed video URL at resolve time and the first mint
  // carries the host's anti-bot wait, so asking now leaves the link cached by
  // the time the provider resolves and playback starts promptly instead of
  // stalling for it. Best-effort; failure is ignored.
  //
  // The /api/vidking/warm call that used to sit at the top of this effect
  // pre-fetched VidLink's own source-API payload; it went away with VidLink
  // (see registry.js).
  useEffect(() => {
    if (!request.tmdbId) return;
    const warmType = request.contentType === ContentType.EPISODE ? "tv" : "movie";
    const qs = new URLSearchParams({ type: warmType, id: String(request.tmdbId) });
    if (warmType === "tv") {
      if (request.season != null) qs.set("season", String(request.season));
      if (request.episode != null) qs.set("episode", String(request.episode));
    }
    fetch(`/api/hosted/resolve?${qs.toString()}`).catch(() => {});
  }, [request.tmdbId, request.contentType, request.season, request.episode]);

  useEffect(() => {
    document.title = request.title ? `${request.title} — PoKu` : "PoKu Player";
    return () => {
      document.title = "PoKu — Watch Movies & TV Shows";
    };
  }, [request.title]);

  // Admin-mapped YouTube uploads play through the IFrame API with the app's
  // own chrome — the same UI as the Vidking player (back, title, seekbar,
  // play/pause, ±10s, mute, fullscreen).
  if (state.source?.kind === "youtube") {
    return <YoutubePlayer source={state.source} request={request} onBack={back} />;
  }

  // Torrent, TorBox, and hosted-file (StreamHG/VidMoly/Cloudflare direct HLS,
  // or a StreamTape progressive mp4) sources play in a native <video> — not the
  // Vidking iframe. "hosted-mp4" differs only in carrying no hlsUrl, which is
  // what tells TorrentVideo to use the plain <video src> path.
  if (
    state.source?.kind === "torrent" ||
    state.source?.kind === "torbox" ||
    state.source?.kind === "hosted" ||
    state.source?.kind === "hosted-mp4"
  ) {
    return (
      <TorrentVideo
        source={{
          url: state.sourceUrl,
          statusUrl: state.source.statusUrl,
          hlsUrl: state.source.hlsUrl || null,
          audio: state.source.audio || null,
          subs: state.source.subs || null,
          // Without this the relay subtitle fallback can never run: the
          // resolved source carries relayBase, but this literal rebuilt the
          // prop and dropped it, so TorrentVideo saw undefined.
          relayBase: state.source.relayBase || null,
        }}
        request={request}
        onBack={back}
        onStreamDead={onHostedStreamDead}
      />
    );
  }

  // Hybrid sources: the video comes from a video host, the audio renditions and
  // subtitles from our own package. Played by our player like a hosted stream
  // (see web/hybrid.js), which is why the host's own flattened audio is muted
  // and only our rendition is audible.
  if (state.source?.kind === "hybrid") {
    return (
      <TorrentVideo
        source={{
          kind: "hybrid",
          url: state.sourceUrl,
          videoUrl: state.source.videoUrl,
          videoHost: state.source.videoHost,
          audioTracks: state.source.audioTracks || [],
          audio: state.source.audio || null,
          offsetMs: state.source.offsetMs || 0,
          subs: state.source.subs || null,
        }}
        request={request}
        onBack={back}
        onStreamDead={onHostedStreamDead}
      />
    );
  }

  // Hosted-file embed sources (Playmate embed, lulustream pages we couldn't
  // extract HLS from) render their own player in a scoped iframe — still PoKu's
  // chrome (back, title, seekbar, CC), not Vidking.
  if (state.source?.kind === "hosted-embed") {
    return (
      <HostedEmbedPlayer
        source={state.source}
        request={request}
        onBack={back}
        onRetry={() => setReloadKey((k) => k + 1)}
      />
    );
  }

  return (
    <EmbedPlayer
      request={request}
      sourceUrl={state.sourceUrl}
      resolving={state.resolving}
      error={state.error}
      switching={switching}
      // Only reserve hosts that were VERIFIED to play inside a sandbox opt in
      // (see RESERVE_EMBED_HOSTS). Withholding top-level navigation matters
      // here: these embeds load directly from the host's own origin, so a
      // click inside one can otherwise navigate PoKu's whole tab away.
      // Demonstrated live 2026-09-24 — Streamrip's Play button redirected the
      // tab to luugy.com and on to an AliExpress affiliate page.
      sandbox={state.source?.sandbox === true}
      onBack={back}
      onRetry={() => setReloadKey((k) => k + 1)}
      // Only embeds that post PLAYER_EVENT get the stall watchdog — SuperEmbed
      // never posts events (their player has no postMessage API), so arming it
      // there would kill every working SuperEmbed stream at the timeout.
      watchdog={state.source?.provider === "vidlink"}
      onStalled={onEmbedStalled}
    />
  );
}

// The Vidking-embed player. Kept as its own component so its hooks live in a
// component that renders unconditionally (hooks must never sit after an early
// return in the resolution shell).
// A host's own player (Playmate embed, or a lulustream page that only exposes
// an embed). Renders the host's iframe inside PoKu's chrome — back + centered
// title + a PoKu fullscreen button — so the essential controls match every
// other provider. Play/pause/seek stay in the host's own overlay (cross-origin
// iframes cannot be scripted); the fullscreen button uses the IFRAME element's
// requestFullscreen, which does work across origins.
function HostedEmbedPlayer({ source, request, onBack }) {
  // Chrome starts hidden (see EmbedPlayer): the host's own bar is visible
  // during boot, so showing ours too doubled the icons at start.
  const [controls, setControls] = useState(false);
  const frameRef = useRef(null);

  // Host embed pages load DIRECTLY from their own origin. Proxied pages
  // (playmate-page route) break: the host's player JS then fetches video
  // segments from OUR origin and the CDN rejects them — the video never
  // plays. Cross-origin iframes keep the host's own session/origin, which is
  // exactly what its CDN expects.
  const iframeUrl = source.embedUrl || source.url;
  const hostLabel = source.host || "host";

  return (
    <div className="player-screen host-embed-player" onClick={() => setControls(true)}>
      <div className="host-embed-top">
        {onBack && (
          <button className="player-back" onClick={onBack} aria-label="Back">
            ←
          </button>
        )}
        {request.title && <span className="host-embed-title">{request.title}</span>}
      </div>
      <iframe
        ref={frameRef}
        className="host-embed-iframe"
        key={iframeUrl}
        src={iframeUrl}
        title={`${request.title || "Video player"} — ${hostLabel}`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen; xr-spatial-tracking"
        allowFullScreen
        // Deliberately NOT sandboxed: Playmate's player refuses to run in any
        // sandboxed frame (their "Sandbox detected" gate) — and their video
        // segments only load from playmate.to's own origin, so their embed is
        // the only playback path. SuperEmbed (the genuinely ad-walled
        // fallback) stays sandboxed in EmbedPlayer below.
      />
      {controls && source.subs?.length ? (
        <div className="host-embed-subs">
          {source.subs.map((t) => (
            <span key={t.label} className="host-embed-sub-label">CC {t.label}</span>
          ))}
        </div>
      ) : null}
      {controls && (
        <div className="host-embed-bottom" onClick={(e) => e.stopPropagation()}>
          {/* hint text removed — the bar carries no controls anymore */}
        </div>
      )}
    </div>
  );
}

// The Vidking-embed player. Kept as its own component so its hooks live in a
// component that renders unconditionally (hooks must never sit after an early
// return in the resolution shell).
function EmbedPlayer({ request, sourceUrl, resolving, error, switching, onBack, onRetry, watchdog, onStalled, sandbox }) {
  const headingRef = useRef(null);
  const lastSavedRef = useRef({ position: -10, at: 0, mediaKey: "" });
  // The embed page starts loading the moment the iframe mounts; the overlay
  // clears on its onLoad event (see below).
  const [pageLoading, setPageLoading] = useState(true);
  const [embedElapsed, setEmbedElapsed] = useState(0);
  useEffect(() => {
    if (!pageLoading) return undefined;
    setEmbedElapsed(0);
    const t = setInterval(() => setEmbedElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [pageLoading]);
  // Flips true on the first REAL playback signal — the stall watchdog checks
  // this. NOT the first PLAYER_EVENT: VidLink's player posts timeupdate
  // events unconditionally (their bundle posts on onTimeUpdate even when no
  // source ever loaded — verified 2026-09-20: Dark Knight at readyState 0
  // still streamed timeupdate every second), so "any event" disarmed the
  // watchdog and a source-less title sat on "Preparing playback…" forever.
  // PLAY means their <video> actually fired a play event; TIME_UPDATE plus a
  // moving clock means frames are actually advancing.
  const gotEventRef = useRef(false);

  // Listen for PLAYER_EVENT messages from the Vidking embed and persist progress
  // exactly like PlayerViewModel.onPlayerEvent() in the Android app.
  useEffect(() => {
    const onMessage = (event) => {
      if (!isVidkingOrigin(event.origin)) return;
      const parsed = parsePlayerEvent(event.data);
      if (!parsed) return;
      if (parsed.tmdbId !== request.tmdbId) return;
      // Only REAL playback counts for the watchdog: PLAY, or TIME_UPDATE
      // whose clock is actually advancing (their source-less player emits
      // timeupdate with currentTime stuck at 0 — see gotEventRef note).
      if (
        parsed.event === "PLAY" ||
        (parsed.event === "TIME_UPDATE" && parsed.currentTimeSeconds > 0)
      ) {
        gotEventRef.current = true;
      }

      const effectiveSeason = parsed.season ?? request.season;
      const effectiveEpisode = parsed.episode ?? request.episode;
      const eventKeyChanged = effectiveSeason !== request.season || effectiveEpisode !== request.episode;
      const mediaKey = `${request.tmdbId}:${effectiveSeason}:${effectiveEpisode}`;
      const last = lastSavedRef.current;
      const contentChanged = mediaKey !== last.mediaKey;

      const now = Date.now();
      const urgent =
        parsed.event === "PAUSE" || parsed.event === "SEEKED" || parsed.event === "ENDED";
      if (
        !urgent &&
        !contentChanged &&
        now - last.at < 5000 &&
        Math.abs(parsed.currentTimeSeconds - last.position) < 5
      ) {
        return;
      }
      last.at = now;
      last.position = parsed.currentTimeSeconds;
      last.mediaKey = mediaKey;

      saveProgress({
        contentType: request.contentType,
        tmdbId: request.tmdbId,
        season: effectiveSeason,
        episode: effectiveEpisode,
        title: request.title,
        subtitle: eventKeyChanged && effectiveSeason != null && effectiveEpisode != null
          ? `S${effectiveSeason} E${effectiveEpisode}`
          : request.subtitle,
        posterPath: request.posterPath,
        backdropPath: request.backdropPath,
        positionSeconds: parsed.event === "ENDED" ? parsed.durationSeconds : parsed.currentTimeSeconds,
        durationSeconds: parsed.durationSeconds,
        updatedAtMillis: now,
        completed: parsed.event === "ENDED",
      });
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [request]);

  // Network-level failure detection for the embed page.
  useEffect(() => {
    if (!sourceUrl) return;
    setPageLoading(true);
    gotEventRef.current = false;
    let cancelled = false;
    fetch(sourceUrl, { mode: "no-cors" }).catch(() => {
      if (!cancelled && error === null) {
        // surfaced via state.error by the parent; nothing to do locally
      }
    });
    return () => {
      cancelled = true;
    };
  }, [sourceUrl, error]);

  // PoKu chrome (top/bottom bars) over the cross-origin player, fading out
  // after 3s idle — the same behavior as the native and Playmate players.
  // Chrome starts HIDDEN while the provider's own player boots (two control
  // stacks visible at once looked broken — PoKu's bars then their bars
  // fading in); it appears on the user's first tap/move and idles away
  // normally after that.
  const [chromeVisible, setChromeVisible] = useState(false);
  const chromeTimerRef = useRef(null);
  useEffect(() => {
    if (!chromeVisible) return undefined;
    clearTimeout(chromeTimerRef.current);
    chromeTimerRef.current = setTimeout(() => setChromeVisible(false), 3000);
    return () => clearTimeout(chromeTimerRef.current);
  }, [chromeVisible]);

  const frameRef = useRef(null);
  // toggleEmbedFullscreen removed — the PoKu fullscreen button was dropped
  // because every embed host ships its own (two fullscreen icons otherwise).
  // frameRef is still used by the iframe itself.

  // Stalled-embed watchdog. VidLink's player shows its own endless spinner
  // when it has no sources for a title and posts NOTHING — no error, no
  // event. If no PLAYER_EVENT arrives within 30s of the embed page loading,
  // report the stall so the parent falls through to the next provider
  // instead of leaving the user on "Preparing playback…" forever. 30s (not
  // 20s) because VidLink's own source resolution legitimately takes 15-25s
  // on some titles (The Dark Knight observed ~26s to first event) — a
  // tighter timeout killed slow starts and pushed them to the SuperEmbed
  // fallback with its ad interstitial. The timeout re-checks gotEventRef so
  // a late-starting stream is safe. 8s is aggressive enough to walk past a
  // dead VidLink before the user gets frustrated, but lenient enough that a
  // cold CDN edge or slow DNS doesn't false-positive.
  useEffect(() => {
    if (!sourceUrl || !watchdog || pageLoading) return undefined;
    if (gotEventRef.current) return undefined;
    const timer = setTimeout(() => {
      if (!gotEventRef.current && onStalled) onStalled();
    }, 8000);
    return () => clearTimeout(timer);
  }, [sourceUrl, watchdog, pageLoading, onStalled]);

  // Resolving: show the loading overlay until the registry picks a source.
  // Show elapsed seconds so the user knows the app isn't frozen.
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!resolving) return undefined;
    setElapsed(0);
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [resolving]);
  if (resolving) {
    return (
      <div className="player-screen">
        <div className="player-overlay">
          <div className="spinner" />
          <p className="status-label">Loading{elapsed > 0 ? ` (${elapsed}s)` : ''}…</p>
        </div>
        <button className="player-back" onClick={onBack} aria-label="Back">←</button>
      </div>
    );
  }

  if (!sourceUrl) {
    return (
      <div className="player-screen">
        <div className="player-error">
          <h2 className="status-title">Playback unavailable</h2>
          <p className="status-label">{providerFriendlyMessage(error)}</p>
          <div className="player-actions">
            <button className="btn btn-primary" onClick={onRetry}>
              TRY AGAIN
            </button>
            <button className="btn btn-ghost" onClick={onBack}>BACK</button>
          </div>
        </div>
      </div>
    );
  }  return (
    <div
      className="player-screen host-embed-player"
      onClick={() => setChromeVisible(true)}
      onMouseMove={() => setChromeVisible(true)}
    >
      <iframe
        key={sourceUrl}
        className="player-iframe"
        src={sourceUrl}
        title={request.title}
        allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
        allowFullScreen
        // Reserve hosts that opted in (source.sandbox) get a sandbox WITHOUT
        // allow-top-navigation and WITHOUT allow-popups, so a click inside the
        // frame can neither navigate PoKu's tab away nor open a popunder.
        // Verified not to break CineSrc playback (see RESERVE_EMBED_HOSTS).
        // Everything else stays unsandboxed — deliberately: VidLink's and
        // Playmate's "sandbox detected" gates kill a sandboxed frame.
        {...(sandbox
          ? { sandbox: "allow-scripts allow-same-origin allow-forms allow-presentation" }
          : {})}
        onLoad={() => setPageLoading(false)}
        ref={frameRef}
        // NO referrerPolicy override: VidLink's media CDNs gate on the
        // vidlink.pro Referer — with no-referrer the stream stalls, the stall
        // watchdog fires, and the movie drops to SuperEmbed (slow start + its
        // ad interstitial). Verified regression 2026-09-19.
        // NO sandbox on any engine: VidLink's "Please Disable Sandbox" gate
        // is FLAKY in sandboxed frames — allow-plugins is not a real sandbox
        // flag (Chromium drops it with a console parse error), their PDF-object
        // probe then fails randomly, and the page dies at the gate. Safari
        // rejects every sandbox set outright (live matrix 2026-09-20). Ads are
        // handled upstream instead: every embed arrives through the same-origin
        // proxy (PopAds stripped, window.open stubbed), so the sandbox's only
        // job — blocking popups — is already done server-side.
      />
      {/* PoKu chrome over the cross-origin player — the same top/bottom bars
          as the Playmate host player (cross-origin frames can't be scripted,
          so play/pause/seek stay inside their player; PoKu provides back,
          centered title, and fullscreen, fading out when idle). */}
      <div className={`host-embed-top${chromeVisible ? "" : " chrome-hidden"}`}>
        {onBack && (
          <button className="player-back" onClick={onBack} aria-label="Back">
            ←
          </button>
        )}
        {request.title && <span className="host-embed-title">{request.title}</span>}
      </div>
      <div
        className={`host-embed-bottom${chromeVisible ? "" : " chrome-hidden"}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* hint text removed — the bar carries no controls anymore. NO PoKu
            fullscreen either: every embed host (VidLink, Playmate, SuperEmbed,
            Lulu) ships its own — a PoKu one rendered TWO fullscreen icons. */}
      </div>
      {pageLoading && !error && (
        <div className="player-overlay">
          <div className="spinner" />
          <p className="status-label">Loading{embedElapsed > 0 ? ` (${embedElapsed}s)` : ''}…</p>
        </div>
      )}
      {switching && (
        <div className="player-overlay">
          <div className="spinner" />
          <p className="status-label">Server unavailable — switching to a backup source…</p>
        </div>
      )}
      {error && (
        <div className="player-overlay">
          <h2 className="status-title">Playback unavailable</h2>
          <p className="status-label">{providerFriendlyMessage(error)}</p>
          <div className="player-actions">
            <button
              className="btn btn-primary"
              onClick={() => {
                onRetry();
              }}
            >
              TRY AGAIN
            </button>
            <button className="btn btn-ghost" onClick={onBack}>BACK</button>
          </div>
        </div>
      )}
      <button className="player-back" onClick={onBack} aria-label="Back">
        ←
      </button>
    </div>
  );
}
